import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

import { Locale } from "@smart/bridge-types-basic";
import { useDebounce } from "@smart/itops-hooks-dom";
import { Checkbox, Select } from "@smart/itops-sb-design-system-dom";
import { Staff } from "@smart/itops-smokeball-app-dom";
import { buildDisplayName } from "@smart/itops-utils-basic";
import { useUpdateSetting } from "@smart/manage-gql-client-dom";

import {
  NotificationSettingFormValues,
  notificationSettingSchema,
} from "./types";

type NotificationSettingProps = {
  localeTerms: Locale["terms"];
  savedNotificationSetting: {
    savedStaffIds: string[];
    savedNotifyCreator: boolean;
    savedNotifyPersonResponsible: boolean;
    savedNotifyPersonAssisting: boolean;
  };
  staffToDisplay: Staff[];
  updateSettings: ReturnType<typeof useUpdateSetting>[0];
};

export const NotificationSetting = ({
  localeTerms,
  staffToDisplay,
  updateSettings,
  savedNotificationSetting,
}: NotificationSettingProps) => {
  const {
    savedStaffIds,
    savedNotifyCreator,
    savedNotifyPersonResponsible,
    savedNotifyPersonAssisting,
  } = savedNotificationSetting;

  const form = useForm<NotificationSettingFormValues>({
    defaultValues: {
      creator: savedNotifyCreator,
      personResponsible: savedNotifyPersonResponsible,
      personAssisting: savedNotifyPersonAssisting,
      otherStaffIds: savedStaffIds,
      displayOtherStaff: savedStaffIds.length > 0,
    },
    resolver: zodResolver(notificationSettingSchema),
  });

  const { control, handleSubmit, watch, getValues, setValue } = form;
  const { displayOtherStaff } = watch();

  const onSubmit = handleSubmit(async (data) => {
    await updateSettings({
      variables: {
        fields: {
          staffToNotify: {
            creator: data.creator,
            personResponsible: data.personResponsible,
            personAssisting: data.personAssisting,
            otherStaffIds:
              getValues("displayOtherStaff") && data.otherStaffIds?.length
                ? data.otherStaffIds
                : [],
          },
        },
      },
    }).catch(console.error);
  });

  const debounced = useDebounce(onSubmit, 1000);

  useEffect(
    () =>
      form.watch(() => {
        debounced();
      }).unsubscribe,
    [],
  );

  return (
    <div className="flex flex-col gap-medium w-full text-ui">
      <div>
        <div className="flex flex-col gap-medium max-w-fit">
          <Controller
            control={control}
            name="creator"
            render={({ field, fieldState }) => (
              <Checkbox
                label="Form Sharer"
                checked={field.value}
                onChange={async (checked) => {
                  field.onChange(checked);
                }}
                dataTestId="form-sharer"
                error={!!fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="personResponsible"
            render={({ field, fieldState }) => (
              <Checkbox
                label={localeTerms.responsiblePerson.title}
                checked={field.value}
                onChange={field.onChange}
                dataTestId="person-responsible"
                error={!!fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="personAssisting"
            render={({ field, fieldState }) => (
              <Checkbox
                label="Person Assisting"
                checked={field.value}
                onChange={field.onChange}
                dataTestId="person-assisting"
                error={!!fieldState.error?.message}
              />
            )}
          />
          <Controller
            control={control}
            name="displayOtherStaff"
            render={({ field, fieldState }) => (
              <Checkbox
                label="Staff"
                checked={field.value}
                onChange={(checked) => {
                  field.onChange(checked);
                  setValue("otherStaffIds", []);
                }}
                dataTestId="display-other-staff"
                error={!!fieldState.error?.message}
              />
            )}
          />
        </div>
        {displayOtherStaff && (
          <Controller
            control={control}
            name="otherStaffIds"
            render={({ field, fieldState }) => (
              <Select
                id="other-staff-ids"
                options={staffToDisplay.map((s) => ({
                  label: buildDisplayName(s, s.userId),
                  value: s.id,
                }))}
                placeholder="Select staff members"
                error={!!fieldState.error?.message}
                value={field.value}
                onChange={async (selectedValues) => {
                  const staffIds = Array.isArray(selectedValues)
                    ? selectedValues
                    : [selectedValues];
                  field.onChange(staffIds as string[]);
                }}
                isMultiple
                className="mt-[0.8rem]"
                dataTestId="select-other-staff"
                empty="No Staff Available"
              />
            )}
          />
        )}
      </div>
    </div>
  );
};
